// Display helpers for the Seller Legal Tracker.

export const DEADLINE_WARNING =
    'Calendar days only — no weekend/holiday roll-forward. Confirm the real last day before relying on a date.';

export function displayName(seller) {
    const name = seller?.storefrontName?.trim();
    return name ? `${name} · ${seller.sellerId}` : seller?.sellerId ?? '';
}

export function deadlineTone(daysToNext) {
    if (daysToNext === null || daysToNext === undefined) return 'none';
    if (daysToNext < 0) return 'overdue';
    if (daysToNext <= 7) return 'soon';
    return 'ok';
}

export function deadlineText(deadline, daysToNext) {
    if (!deadline) return 'No open deadline';
    let when;
    if (daysToNext < 0) when = `${-daysToNext} day${daysToNext === -1 ? '' : 's'} overdue`;
    else if (daysToNext === 0) when = 'today';
    else when = `in ${daysToNext} day${daysToNext === 1 ? '' : 's'}`;
    return `${deadline.label} ${deadline.due} (${when})`;
}

export const TONE_CLASSES = {
    overdue: 'text-red-700 font-semibold',
    soon: 'text-amber-700 font-semibold',
    ok: 'text-gray-700',
    none: 'text-gray-400',
};

export const storefrontUrl = (sellerId) => `https://www.amazon.com/sp?seller=${sellerId}`;
export const sellerListingUrl = (asin, sellerId) => `https://www.amazon.com/dp/${asin}?m=${sellerId}`;

const nextDay = (iso) => {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
};

// One point per calendar day from the first daily row to the last (or `through`, if later).
// A day with no row is null: the offer feed recorded nothing, so it draws as a gap, never as 0.
export function buildTimeline(daily, through) {
    if (!daily || daily.length === 0) return { labels: [], values: [] };
    const byDay = new Map(daily.map((d) => [String(d.day), d.asinCount]));
    const first = String(daily[0].day);
    const lastRow = String(daily[daily.length - 1].day);
    const last = through && through > lastRow ? through : lastRow;
    const labels = [];
    const values = [];
    for (let d = first; d <= last; d = nextDay(d)) {
        labels.push(d);
        values.push(byDay.has(d) ? byDay.get(d) : null);
    }
    return { labels, values };
}

export function timelineSummary(daily) {
    if (!daily || daily.length === 0) return null;
    return {
        daysListed: daily.filter((d) => d.asinCount > 0).length,
        peak: Math.max(...daily.map((d) => d.asinCount)),
        current: daily[daily.length - 1].asinCount,
        from: String(daily[0].day),
        to: String(daily[daily.length - 1].day),
    };
}

// ---- Board ---------------------------------------------------------------------------------

const STAGE_TONES = {
    DETECTED: 'gray', REVIEWING: 'gray',
    PURCHASE_ORDERED: 'blue', EVIDENCE_COMPLETE: 'blue',
    NOTICE_SENT: 'amber', CURE_ENDED: 'amber',
    FILED: 'red', SERVED: 'red',
    ORDER_OR_JUDGMENT: 'green', MONITORING: 'green',
    CLOSED: 'slate',
};

export const stageTone = (stage) => STAGE_TONES[stage] || 'gray';

export const TONE_BADGE = {
    gray: 'bg-gray-100 text-gray-700 ring-gray-500/20',
    blue: 'bg-blue-50 text-blue-700 ring-blue-600/20',
    amber: 'bg-amber-50 text-amber-800 ring-amber-600/20',
    red: 'bg-red-50 text-red-700 ring-red-600/20',
    green: 'bg-green-50 text-green-700 ring-green-600/20',
    slate: 'bg-slate-100 text-slate-500 ring-slate-500/20',
};

const isOpen = (r) => r.seller.stage !== 'CLOSED';
const isDueSoon = (r) => r.daysToNext !== null && r.daysToNext !== undefined && r.daysToNext <= 7;

/** The four summary tiles. Closed sellers are not counted. */
export function boardCounts(rows) {
    const open = rows.filter(isOpen);
    return {
        needsAction: open.filter((r) => r.flags.length > 0).length,
        listingNow: open.filter((r) => r.seller.active).length,
        dueSoon: open.filter(isDueSoon).length,
        tracked: open.length,
    };
}

export function stageCounts(rows) {
    const c = {};
    rows.forEach((r) => { c[r.seller.stage] = (c[r.seller.stage] || 0) + 1; });
    return c;
}

const TILE_FILTERS = {
    needsAction: (r) => r.flags.length > 0,
    listingNow: (r) => r.seller.active,
    dueSoon: isDueSoon,
    tracked: () => true,
};

/** stage: one stage (incl. CLOSED); otherwise closed sellers are hidden. tile and query narrow further. */
export function filterRows(rows, { stage, tile, query } = {}) {
    const q = (query || '').trim().toLowerCase();
    return rows.filter((r) => {
        if (stage ? r.seller.stage !== stage : !isOpen(r)) return false;
        if (tile && TILE_FILTERS[tile] && !TILE_FILTERS[tile](r)) return false;
        if (q && !displayName(r.seller).toLowerCase().includes(q)) return false;
        return true;
    });
}

const SORT_KEYS = {
    seller: (r) => displayName(r.seller).toLowerCase(),
    stage: (r) => r.seller.stage,
    asins: (r) => r.asinCount,
    listing: (r) => (r.seller.active ? 1 : 0),
    lastSeen: (r) => r.seller.lastSeen,
    deadline: (r) => r.daysToNext,
};

/** No key keeps the server's order (flagged → nearest deadline → priority → newest). Nulls sort last. */
export function sortRows(rows, key, dir = 'asc') {
    if (!key || !SORT_KEYS[key]) return rows;
    const get = SORT_KEYS[key];
    const sign = dir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
        const x = get(a);
        const y = get(b);
        if (x === null || x === undefined) return (y === null || y === undefined) ? 0 : 1;
        if (y === null || y === undefined) return -1;
        return (x < y ? -1 : x > y ? 1 : 0) * sign;
    });
}

// ---- ASIN trend (board) --------------------------------------------------------------------

const shortDay = (iso) => new Date(`${iso}T00:00:00Z`)
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });

/**
 * Arrow next to a seller's ASIN count: latest vs 30 days earlier (7 when the seller is younger).
 * More listings is worse for us, so up is red and down is green.
 */
export function trendIndicator(trend) {
    if (!trend) return null;
    const change = trend.latestCount - trend.baseCount;
    const dir = change > 0 ? 'up' : change < 0 ? 'down' : 'flat';
    const window = trend.windowDays === 30 ? '' : `${trend.windowDays}d`;
    const span = trend.windowDays === 30 ? '30 days' : `${trend.windowDays} days — seller is under 30 days old`;
    return {
        symbol: { up: '▲', down: '▼', flat: '▬' }[dir],
        text: change === 0 ? '' : String(Math.abs(change)),
        className: { up: 'text-red-600', down: 'text-green-600', flat: 'text-gray-400' }[dir],
        window,
        title: `${trend.latestCount} ASINs now vs ${trend.baseCount} on ${shortDay(trend.baseDay)} (${span})`,
    };
}
